import React from 'react';
import {WordCloudData} from 'entities/event';
import {ChartQuery} from 'entities/chart';
import {AutoSizer} from 'react-virtualized';
import {chartColors} from '@/views/storyboard/utils/chartConstants';
import {serialize} from '@/views/storyboard/utils/helpers';
import {Text} from '@visx/text';
import {scaleLog} from '@visx/scale';
import {Wordcloud} from '@visx/wordcloud';

interface Props {
    data: WordCloudData[];
    setTooltipText: (tooltipText: WordCloudData) => void;
    // @ts-ignore
    setTooltipCoords: (tooltipCoords: TooltipCoords) => void;
    finalChartQuery: ChartQuery | {};
    onWordMouseOver: (event: React.MouseEvent<HTMLDivElement>) => void;
    onWordMouseLeave: () => void;
}

export const WordCloud = React.memo<Props>(
    React.forwardRef<HTMLDivElement, Props>(
        ({
            data,
            setTooltipText,
            setTooltipCoords,
            finalChartQuery,
            onWordMouseOver,
            onWordMouseLeave,
        }, ref) => {

            const words = data.map(([text, value]) => ({text, value}));

            const fontScale = scaleLog({
                domain: [Math.min(...words.map((w) => w.value)), Math.max(...words.map((w) => w.value))],
                range: [10, 85],
            });

            const fontSizeSetter = (datum: { value: number }) => fontScale(datum.value);
            const getRotationDegree = () => 0;
            const fixedValueGenerator = () => 0.5;

            const handleClick = (item: { text: string | undefined }) => {
                if (Object.keys(finalChartQuery).length === 0) {
                    return;
                }
                const chartQuery = finalChartQuery as ChartQuery;
                const defChartQuery = {...chartQuery};

                if (!item) {
                    setTooltipText(['', 0]);
                    return;
                }

                const selectedWord = item.text;

                defChartQuery.query_string = `(${chartQuery.query_string}) AND "${selectedWord}"`;

                const url = `${window.URLS.searchUrl}?${serialize(defChartQuery)}`;
                window.open(url, '_blank');
            }

            return (
                <AutoSizer>
                    {({height = 0, width = 0}) => (
                        <div ref={ref} onMouseOver={(event) => onWordMouseOver(event)} onMouseLeave={onWordMouseLeave}>
                            <Wordcloud
                                words={words}
                                width={width}
                                height={height}
                                fontSize={fontSizeSetter}
                                fontWeight={'500'}
                                padding={7}
                                spiral={'rectangular'}
                                rotate={getRotationDegree}
                                random={fixedValueGenerator}
                            >
                                {(cloudWords) =>
                                    cloudWords.map((w, i) => (
                                        <Text
                                            key={w.text}
                                            fill={chartColors[i % chartColors.length]}
                                            textAnchor={'middle'}
                                            transform={`translate(${w.x || 0}, ${w.y || 0}) rotate(${w.rotate || 0})`}
                                            fontSize={w.size}
                                            fontFamily={'Trebuchet MS'}
                                            fontWeight={'500'}
                                            onClick={() => handleClick({text: w.text})}>
                                            {w.text}
                                        </Text>
                                    ))
                                }
                            </Wordcloud>
                        </div>
                    )}
                </AutoSizer>
            );
        }));

